import { computed, toValue } from 'vue'
import type { ComputedRef, MaybeRefOrGetter } from 'vue'
import { useI18n } from 'vue-i18n'
import type { AgentInterface } from 'models'

/**
 * Resolves the greeting shown in the fullscreen empty state.
 *
 * v1 behavior (docs/specs/public-chat-page.md): uses the first entry of the
 * `agentInterface.greeting` pool and supports only the `{name}` placeholder.
 * Fallback chain: `greeting[0]` → `initialMessage` → default welcome message.
 * Pool rotation and `{timeGreeting}` are planned follow-ups.
 */
export function useGreeting(
    agentInterface: MaybeRefOrGetter<AgentInterface | undefined>,
    userName: MaybeRefOrGetter<string | undefined>,
): ComputedRef<string> {
    const { locale, t, te } = useI18n({ useScope: 'global' })

    return computed(() => {
        const ui = toValue(agentInterface)
        const template =
            ui?.greeting?.[0]?.[locale.value] ||
            ui?.initialMessage?.[locale.value] ||
            (te('message.defaultAgentWelcome')
                ? t('message.defaultAgentWelcome')
                : '')
        return resolveNamePlaceholder(template, toValue(userName))
    })
}

function resolveNamePlaceholder(template: string, name?: string): string {
    const result = name
        ? template.replace(/\{name\}/g, name)
        : // No name available: drop the placeholder along with any
          // adjacent punctuation (", {name}!" → "")
          template.replace(/[,\s]*\{name\}[!?]?/g, '')
    return result.trim()
}
